// src/app/services/settings-service.ts
import { apiFetch } from "@/api/api-fetch";

export interface UserProfile {
  fullName: string;
  email: string;
  emailVerified: boolean;
  jobTitle: string;
  company: string;
  timezone: string;
  language: string;
}

export interface NotificationPreferences {
  analysisReports: boolean;
  weeklyDigest: boolean;
  productUpdates: boolean;
}

export interface PrivacyPreferences {
  usageAnalytics: boolean;
}

export interface AccessibilityPreferences {
  fontSize: number; // 14..20
  reducedMotion: boolean;
  highContrast: boolean;
}

/**
 * Layout mode for the Suggested Actions tab.
 * - "split"     = two-pane (list + detail), current default
 * - "accordion" = single-column, multiple rows can be expanded simultaneously
 */
export type InboxLayout = "split" | "accordion";

export interface AppearancePreferences {
  theme: "light" | "dark";
  numberFormat: string;
  dateFormat: string;
  decimalPrecision: number;
  startNavCollapsed: boolean;
  startAiCollapsed: boolean;

  /** NEW — Suggested Actions view mode */
  inboxLayout?: InboxLayout;
}

export interface BillingPlan {
  planName: string;
  planPrice: string;
  renewalDate: string;
  features: {
    aiModels: string;
    apiCalls: string;
    teamMembers: string;
  };
}

export interface PaymentMethod {
  type: string;
  last4: string;
  expiryDate: string;
}

export interface SettingsData {
  profile: UserProfile;
  notifications: NotificationPreferences;
  privacy: PrivacyPreferences;
  accessibility: AccessibilityPreferences;
  appearance: AppearancePreferences;

  billing: BillingPlan;
  paymentMethod: PaymentMethod;

  security?: any;
  apiKey?: any;
}

export type SettingsPatch = Partial<SettingsData>;

export async function getSettings(): Promise<SettingsData> {
  return apiFetch<SettingsData>("/settings", { method: "GET" });
}

export async function patchSettings(patch: SettingsPatch): Promise<SettingsData> {
  return apiFetch<SettingsData>("/settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}